import {
  HttpBackend,
  HttpClient,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { jwtDecode } from 'jwt-decode';
import {
  BehaviorSubject,
  Observable, Subject, catchError, finalize, firstValueFrom, map, of, shareReplay,
  tap,
} from 'rxjs';

import { ampli } from '../ampli/index';
import { AnalyticsService } from '../analytics/analytics.service';
import { ENVIRONMENT } from '../environments/environment.token';
import { safeJson } from '../helpers/json.helper';
import {
  DecodedToken, LoginResponse, LoginSessionInfo, User,
} from '../models/user.model';


@Injectable({
  providedIn: 'root',
})
export class AuthService {

  user$: Observable<User | null>;
  login$: Observable<void>;

  protected httpBackend = inject(HttpBackend);
  protected http = new HttpClient(this.httpBackend);
  protected userSubject = new BehaviorSubject<User | null>(null);
  protected loginSubject = new Subject<void>();

  private sessionInfoKey = 'sessionInfo';
  private rememberMeKey = 'rememberMe';
  private baseUrl = inject(ENVIRONMENT).apiAuthUrl;
  private appName = inject(ENVIRONMENT).appName;
  private router = inject(Router);
  private analyticsService = inject(AnalyticsService);
  private refreshInFlight$: Observable<User | null> | null = null;

  constructor(
  ) {
    this.user$ = this.userSubject.asObservable();
    this.login$ = this.loginSubject.asObservable();

    this.userSubject.subscribe(user => {
      if (user && user.email) {
        this.analyticsService.setUserId(user.email);
        this.analyticsService.setUserProperties(user.email, {
          Customer: user.customerId,
          user_role: user.userRole,
        });
      }
    });
    this.loadSession();
  }

  getUser(): User | null {
    return this.userSubject.value;
  }

  isExpert(): boolean {
    return !this.userSubject.value?.customerId;
  }

  getAuthToken(): string {
    const raw = this.getSessionInfo();
    if (!raw) return '';
    const result = safeJson<LoginSessionInfo>(raw);

    return result.ok ? result.value.authToken ?? '' : '';
  }

  login(email: string, password: string, rememberMe: boolean): Observable<User | null> {
    const url = `${this.baseUrl}/auth/login`;

    return this.http.post<LoginResponse>(url, { email, password }).pipe(
      map(response => {
        this.saveSessionInfo(response.sessionInfo, rememberMe);

        return this.decodeToken(response.sessionInfo.authToken);
      }),
      tap(user => {
        this.userSubject.next(user);
        if (user) {
          this.loginSubject.next();
          ampli.login({ method: 'password' });
        }
      }),
    );
  }

  // Errors are propagated to the caller so the login page can show them.
  googleLogin(token: string): Observable<User | null> {
    const url = `${this.baseUrl}/auth/google`;

    return this.http.post<LoginResponse>(url, { token }).pipe(
      map(response => {
        this.saveSessionInfo(response.sessionInfo, false);

        return this.decodeToken(response.sessionInfo.authToken);
      }),
      tap(user => {
        this.userSubject.next(user);
        if (user) {
          this.loginSubject.next();
          ampli.login({ method: 'google' });
        }
      }),
    );
  }

  // Errors are propagated to the caller so the login page can show them.
  microsoftLogin(token: string): Observable<User | null> {
    const url = `${this.baseUrl}/auth/microsoft`;

    return this.http.post<LoginResponse>(url, { token }).pipe(
      map(response => {
        this.saveSessionInfo(response.sessionInfo, false);

        return this.decodeToken(response.sessionInfo.authToken);
      }),
      tap(user => {
        this.userSubject.next(user);
        if (user) {
          this.loginSubject.next();
          ampli.login({ method: 'microsoft' });
        }
      }),
    );
  }

  async logout() {
    this.clearSessionInfo();
    // await firstValueFrom(this.revokeToken());
    this.userSubject.next(null);
    this.router.navigate([ './login' ]);
  }

  // Always succeeds on the backend, whether or not the email exists (no user enumeration).
  // The app name selects the reset page URL configured on the backend; the link is never
  // built from anything the client sends.
  requestPasswordReset(email: string, language: string): Observable<void> {
    const url = `${this.baseUrl}/auth/password-reset`;

    return this.http.post<void>(url, {
      email, language, app: this.appName,
    }).pipe(
      tap(() => ampli.passwordResetRequested()),
    );
  }

  // Errors are propagated to the caller so the reset page can distinguish
  // expired/invalid codes (problem title: Auth.ResetCodeExpired / Auth.ResetCodeInvalid).
  confirmPasswordReset(tenantId: string, code: string, newPassword: string): Observable<void> {
    const url = `${this.baseUrl}/auth/password-reset/confirm`;

    return this.http.post<void>(url, {
      tenantId, code, newPassword,
    }).pipe(
      tap(() => ampli.passwordResetCompleted()),
    );
  }

  // Concurrent callers (every request the interceptor sees with an expired token) share one
  // in-flight refresh instead of each spending the refresh token on its own call.
  refreshToken(): Observable<User | null> {
    this.refreshInFlight$ ??= this.requestRefresh().pipe(
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this.refreshInFlight$;
  }

  revokeToken(): Observable<boolean> {
    const url = `${this.baseUrl}/auth/revoke`;
    const user = this.userSubject.value;

    if (user && user.email) {
      return this.http.post(url, { email: user.email }, { observe: 'response' })
        .pipe(
          map((response: HttpResponse<any>) => {
            return response.status === 200;
          }),
          catchError(() => {
            return of(false);
          })
        );
    }

    return of(false);
  }

  // private fetchUserWithToken(token: string): Observable<User | null> {
  //   const url = `${this.baseUrl}/users/current`;
  //   // const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

  //   console.log('fetchUserWithToken >>>');

  //   return this.http.get<UserResponse>(url).pipe(
  //     map(user => ({ ...user.user, token }))
  //     // catchError(() => of(null))
  //   );
  // }

  protected decodeToken(token: string): User | null {
    if (token) {
      const data = jwtDecode<DecodedToken>(token);

      return {
        ...data.instaclause,
        email: data.email,
        displayName: data.name,
        token,
        tokenExpiration: data.exp,
      } as User;
    }

    return null;
  }

  protected saveSessionInfo(sessionInfo: LoginSessionInfo, rememberMe: boolean): void {
    const storage = rememberMe ? localStorage : sessionStorage;

    storage.setItem(this.sessionInfoKey, JSON.stringify(sessionInfo));
    storage.setItem(this.rememberMeKey, JSON.stringify(rememberMe));
  }

  private requestRefresh(): Observable<User | null> {
    const url = `${this.baseUrl}/auth/refresh`;
    const sessionInfo = this.getSessionInfo();

    if (!sessionInfo) {
      return of(null);
    }

    const parsedSessionInfo: LoginSessionInfo = JSON.parse(sessionInfo);
    const rememberMe = this.verifyRememberMe();
    const params = new HttpParams().set('refreshToken', parsedSessionInfo.refreshToken);

    return this.http.get<LoginResponse>(url, { params }).pipe(
      map(newSessionInfo => {
        this.saveSessionInfo(newSessionInfo.sessionInfo, rememberMe);

        return this.decodeToken(newSessionInfo.sessionInfo.authToken);
      }),
      tap(user => this.userSubject.next(user)),
      catchError((error) => {
        console.error('{ Auth Service - refreshToken() }: ERROR', error);
        this.logout();

        return of(null);
      }),
    );
  }

  private async loadSession() {
    const sessionInfo = this.getSessionInfo();

    if (sessionInfo) {
      const parsedSessionInfo: LoginSessionInfo = JSON.parse(sessionInfo);
      const user = this.decodeToken(parsedSessionInfo.authToken);

      if (user && user.tokenExpiration && user.tokenExpiration > Date.now() / 1000) {
        this.userSubject.next(user);
      } else {
        await firstValueFrom(this.refreshToken());
      }
    }
  }

  private getSessionInfo(): string | null {
    return localStorage.getItem(this.sessionInfoKey) || sessionStorage.getItem(this.sessionInfoKey);
  }

  private verifyRememberMe(): boolean {
    const res = localStorage.getItem(this.rememberMeKey) ||
      sessionStorage.getItem(this.rememberMeKey);

    return res ? JSON.parse(res) : false;
  }

  private clearSessionInfo() {
    localStorage.removeItem(this.sessionInfoKey);
    sessionStorage.removeItem(this.sessionInfoKey);
  }
}
